import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  AsyncTaskInfoDto,
  AsyncTaskLogInfoDto,
  MyUserAccountService,
  ResponseArgsOfAsyncTaskInfoDto,
  ScheduleGroupService,
} from '@swagger/humanresources';
import { toBackendErrorReason, toBackendFailureReason } from '@shared/utils';
import {
  MAX_LISTED_PLANNING_TASKS,
  PlanningTaskRejectedError,
  PlanningTaskType,
  StartedPlanningTask,
} from '@features/planning/util-planning-task';

/**
 * The task's message with its references (unit, schedule group, contributors)
 * tells which plan a task works on; hrp-test fills them from level 3 on,
 * level 1 gives the message without references (#5182).
 */
const EAGER_LOADING_WITH_REFERENCES = 3;

/** The list is a page; newest first keeps a running task inside it (#5182). */
const CREATED = 'created';

/** Server calls of the planning tasks (backend #5181): start one, read its logs, list the running ones. */
@Injectable({ providedIn: 'root' })
export class PlanningTaskClient {
  #scheduleGroups = inject(ScheduleGroupService);
  #myAccount = inject(MyUserAccountService);

  /** Starts finalizing a schedule group in the background; resolves with the task once it is created. */
  async startFinalize(scheduleGroupUId: string): Promise<StartedPlanningTask> {
    let response: ResponseArgsOfAsyncTaskInfoDto;
    try {
      response = await firstValueFrom(
        this.#scheduleGroups.scheduleGroupFinalize({ scheduleGroupUId }),
      );
    } catch (error) {
      // A 400 with `message` is the rejection, not a validation error (#5182).
      const reason = toBackendErrorReason(error);
      if (reason) throw new PlanningTaskRejectedError(reason);
      throw error;
    }
    const task = response.result;
    if (response.error || !task || !isStartedTask(task)) {
      throw new PlanningTaskRejectedError(toBackendFailureReason(response));
    }
    return task;
  }

  /** The task's logs after `after` (exclusive, UTC), oldest first: progress and, at the end, the milestone. */
  async fetchLogs(asyncTaskUId: string, after: string | null): Promise<AsyncTaskLogInfoDto[]> {
    const response = await firstValueFrom(
      this.#myAccount.myUserAccountGetAsyncTaskLogs({
        asyncTaskUId,
        after: after ?? undefined,
        onlyMilestones: false,
      }),
    );
    return response.result ?? [];
  }

  /**
   * The user's tasks of `type` without a milestone, with their references. The
   * `type` filter key is unconfirmed (#5248), so the result is filtered here too.
   */
  async listRunningTasks(type: PlanningTaskType): Promise<StartedPlanningTask[]> {
    const response = await firstValueFrom(
      this.#myAccount.myUserAccountQueryAsyncTasks({
        eagerLoading: EAGER_LOADING_WITH_REFERENCES,
        body: {
          filter: { type },
          orderBy: [{ by: CREATED, desc: true }],
          take: MAX_LISTED_PLANNING_TASKS,
        },
      }),
    );
    return (response.result ?? [])
      .filter(isStartedTask)
      .filter((task) => task.type === type && task.latestMilestone == null);
  }
}

function isStartedTask(task: AsyncTaskInfoDto): task is StartedPlanningTask {
  return !!task.uId;
}
